import {
  gql,
  useApolloClient,
  useLazyQuery,
  useMutation
} from '@apollo/client';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GET_TOTAL_CART_PRODUCTS } from '../common/SharedQueries';
import {
  hideLoader,
  loginAsync,
  loginSuccess,
  logoutAsync,
  resetLocalCartItem,
  saveUserAsync,
  showLoader
} from '../store/actions';
import {
  segmentIdentifyCall
} from '@/utils/segmentTracking';

const formatUser = user => {
  const isCustomer = user.role_id == '1' || user.role_id == '2' || user.role_id == '6';
  const isAdmin = user.role_id == '5';
  return {
    ...user,
    isCustomer,
    isAdmin
  };
};

export const formatLocalCart = localCart => {
  return JSON.stringify(
    localCart
      .filter(y => y.product_id)
      .map(x => {
        return {
          quantity: x.quantity,
          product_id: x.product_id,
          shipping_service_id: x.shipping_service_id,
          type: 'increase',
          source: x.source,
          extend_plan_id: x.extend_plan_id
        };
      })
  );
};

export const formatLocalCartGiftCards = localCart => {
  return JSON.stringify(localCart.filter(y => y.giftCard).map(x => x.giftCard));
};

const useAuth = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const client = useApolloClient();
  const user = useSelector(state => state.Auth.user);
  const localCart = useSelector(state => state.LocalCart.localCart);
  const isAuthenticated = useSelector(state => state.Auth.isAuthenticated);
  const errorAxios = useSelector(state => state.Auth.error);

  const [addGiftCardsToCart] = useMutation(CREATE_CART_PRODUCT_GIFT_CARDS);

  const [
    addLocalCart,
    { error: errorAddToCart, loading: loadingAddToCart }
  ] = useMutation(CREATE_CART_PRODUCTS, {
    refetchQueries: [
      { query: GET_TOTAL_CART_PRODUCTS, fetchPolicy: 'network-only' }
    ],
    onCompleted() {
      dispatch(resetLocalCartItem());
    },
    variables: {
      cartProducts: formatLocalCart(localCart)
    }
  });

  const [
    getMe,
    { data, loading: loadingQuery, error: errorQuery }
  ] = useLazyQuery(ME, {
    fetchPolicy: 'network-only',
    onError() {
      console.log(errorQuery);
      dispatch(hideLoader());
    },
    async onCompleted(data) {
      if (data && data.me) {
        if (data.me.is_shipping_cost_exempt) {
          window.variantForFreeShippingExperiment = 0;
        }
        try {
          await dispatch(saveUserAsync(formatUser(data.me)));

          segmentIdentifyCall(data.me.id, {
            firstName: data.me.first_name,
            lastName: data.me.last_name,
            email: data.me.email,
            emails_ok: data.me.emails_ok
          });
          
          if (localCart.some(x => x.giftCard)) {
            await addGiftCardsToCart({
              variables: {
                cart_product_gif_cards: formatLocalCartGiftCards(localCart),
                user_id: data.me?.id
              }
            });
          }

          if (localCart.some(x => x.product_id)) {
            await addLocalCart();
          }

          await dispatch(hideLoader());
          setLoading(false);
          dispatch(loginSuccess(true));
        } catch (err) {
          console.log(err);
        }
      }
    }
  });

  const logout = async () => {
    try {
      await dispatch(logoutAsync());
      client.resetStore();
    } catch (err) {
      console.log(err);
    }
  };

  const login = async (email, password) => {
    dispatch(showLoader());
    setLoading(true);
    try {
      await dispatch(loginAsync(email, password));
      await getMe();
    } catch (err) {
      logout();
      console.log(err);
    }
  };

  const error = errorAxios || errorQuery;

  return [
    login,
    logout,
    {
      loading,
      error,
      data: data,
      isAuthenticated
    }
  ];
};

export default useAuth;

const ME = gql`
  query me {
    me {
      id
      first_name
      last_name
      email
      is_shipping_cost_exempt
      role_id
      is_cx_agent
      created_at
      emails_ok
    }
  }
`;

const CREATE_CART_PRODUCTS = gql`
  mutation createCartProducts($cartProducts: String!) {
    createCartProducts(cartProducts: $cartProducts) {
      id
    }
  }
`;

const CREATE_CART_PRODUCT_GIFT_CARDS = gql`
  mutation createCartProducts($user_id: ID!, $cart_product_gif_cards: String!) {
    createCartProductGiftCards(
      user_id: $user_id
      cart_product_gif_cards: $cart_product_gif_cards
    ) {
      id
    }
  }
`;
